"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Text,
  FormErrorMessage,
  useToast,
  HStack,
  Icon,
  ModalCloseButton,
} from "@chakra-ui/react";
import { TbCalendarSearch } from "react-icons/tb";
import { TimezoneSelect } from "./TimezoneSelect";

interface CalendarAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any) => void;
  initialValues?: any;
}

export const CalendarAvailabilityModal: React.FC<
  CalendarAvailabilityModalProps
> = ({ isOpen, onClose, onSave, initialValues }) => {
  const [name, setName] = useState("check_calendar_availability");
  const [description, setDescription] = useState(
    "When users ask for availability, check the calendar and provide available slots."
  );
  const [apiKey, setApiKey] = useState("");
  const [eventTypeId, setEventTypeId] = useState("");
  const [timezone, setTimezone] = useState("");
  const [nameError, setNameError] = useState("");
  const toast = useToast();

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "check_calendar_availability");
      setDescription(
        initialValues.description ||
          "When users ask for availability, check the calendar and provide available slots."
      );
      setApiKey(initialValues.cal_api_key || "");
      setEventTypeId(
        initialValues.event_type_id?.toString() ||
          initialValues.cal_event_type_id ||
          ""
      );
      setTimezone(initialValues.timezone || "America/Los_Angeles");
    } else {
      setName("check_calendar_availability");
      setDescription(
        "When users ask for availability, check the calendar and provide available slots."
      );
      setApiKey("");
      setEventTypeId("");
      setTimezone("America/Los_Angeles");
    }
  }, [initialValues]);

  const validateToolName = (value: string) => {
    if (!value.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }
    if (value.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(value)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }
    setNameError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  const handleSave = () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    onSave({
      type: "check_availability_cal",
      name,
      description,
      cal_api_key: apiKey,
      event_type_id: Number(eventTypeId),
      timezone,
    });
  };

  const blueFocus = {
    borderColor: "blue.500",
    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent
        bg="white"
        color="gray.800"
        border="1px"
        borderColor="blue.200"
        borderRadius="xl"
        _focusWithin={{
          borderColor: "blue.300",
          boxShadow: "0 0 0 1px var(--chakra-colors-blue-300)",
        }}
      >
        <ModalHeader
          display="flex"
          alignItems="center"
          gap={2}
          color="gray.800"
        >
          <HStack spacing={2}>
            <Icon as={TbCalendarSearch} boxSize={5} color="blue.600" />
            <span>Check Calendar Availability (Cal.com)</span>
          </HStack>
        </ModalHeader>

        <ModalCloseButton
          _hover={{ bg: "blue.50", color: "blue.700" }}
          _active={{ bg: "blue.100" }}
          _focusVisible={{
            boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
          }}
        />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel color="gray.800">Name</FormLabel>
              <Input
                id="check-calendar-availability-function-name"
                value={name}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                placeholder="Enter tool name"
                variant="outline"
                bg="white"
                color="gray.800"
                borderColor="gray.300"
                _hover={{ borderColor: "blue.300" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="blue.400"
                _focus={blueFocus}
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl>
              <FormLabel color="gray.800">Description (Optional)</FormLabel>
              <Textarea
                id="check-calendar-availability-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter function description"
                variant="outline"
                bg="white"
                color="gray.800"
                borderColor="gray.300"
                _hover={{ borderColor: "blue.300" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="blue.400"
                _focus={blueFocus}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="gray.800">API Key (Cal.com)</FormLabel>
              <Input
                id="check-calendar-availability-function-api-key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter Cal.com API key"
                type="password"
                variant="outline"
                bg="white"
                color="gray.800"
                borderColor="gray.300"
                _hover={{ borderColor: "blue.300" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="blue.400"
                _focus={blueFocus}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="gray.800">Event Type ID (Cal.com)</FormLabel>
              <Input
                id="check-calendar-availability-function-event-type-id"
                value={eventTypeId}
                onChange={(e) => setEventTypeId(e.target.value)}
                placeholder="Enter Event Type ID"
                variant="outline"
                bg="white"
                color="gray.800"
                borderColor="gray.300"
                _hover={{ borderColor: "blue.300" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="blue.400"
                _focus={blueFocus}
              />
              <Text fontSize="sm" color="gray.600" mt={1}>
                Must be a numeric value (e.g., 2505265)
              </Text>
            </FormControl>

            <TimezoneSelect
              value={timezone}
              onChange={setTimezone}
              label="Timezone (Optional)"
              placeholder="Search or select timezone (e.g., America/Los_Angeles)"
            />
            <Text fontSize="sm" color="gray.600" mt={-2}>
              Default: America/Los_Angeles
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            color="black"
            _hover={{ bg: "gray.100" }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            colorScheme="blue"
            bg="blue.500"
            color="white"
            _hover={{ bg: "blue.600" }}
            _active={{ bg: "blue.700" }}
            _focusVisible={{
              boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
            }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
